import { DatasetType, ViewType } from 'mapistry-shared';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { LinkEnd } from './types';

type OrgSpecificLink<PathEnd extends LinkEnd> =
  `/vortex/organizations/${string}${PathEnd}`;

type GetLinkOnSameOrg = <PathEnd extends LinkEnd>(
  pathEnd: PathEnd,
) => OrgSpecificLink<PathEnd>;

interface OrgSpecificLinkProps<PathEnd> {
  organizationId: string;
  pathEnd: PathEnd;
}

enum DataSourceTypeUrl {
  LOGS = 'logs',
  RESOURCE_TYPES = 'resource-types',
  WORKFLOWS = 'workflows',
}

const datasetTypeToDataSourceTypesMap: Partial<
  Record<DatasetType, DataSourceTypeUrl>
> = {
  [DatasetType.LOG]: DataSourceTypeUrl.LOGS,
  [DatasetType.RESOURCE_TYPE]: DataSourceTypeUrl.RESOURCE_TYPES,
  [DatasetType.WORKFLOW]: DataSourceTypeUrl.WORKFLOWS,
};

export function getDataSourceTypeForLink({
  viewType,
  datasetType,
}: {
  viewType?: ViewType;
  datasetType?: DatasetType;
}): DataSourceTypeUrl | undefined {
  if (!viewType) return undefined;
  if (viewType === ViewType.LOG) return DataSourceTypeUrl.LOGS;
  if (datasetType) return datasetTypeToDataSourceTypesMap[datasetType];
  return undefined;
}

export function getOrgSpecificLink<PathEnd extends LinkEnd>(
  props: OrgSpecificLinkProps<PathEnd>,
): OrgSpecificLink<PathEnd> {
  const { organizationId, pathEnd } = props;
  return `/vortex/organizations/${organizationId}${pathEnd}`;
}

function useOrgFromPath() {
  const { organizationId } = useParams();

  if (!organizationId) {
    throw new Error(
      "useOrgFromPath page was used from a url that doesn't have organizationId",
    );
  }

  return { organizationId };
}

function useLogIdFromPath() {
  const { logId } = useParams();

  if (!logId) {
    throw new Error(
      "useLogIdFromPath page was used from a url that doesn't have logId",
    );
  }
  return { logId };
}

/** To help easily navigate between different sub pages on the same org, while avoiding relative links. */
export const useLinkOnSameOrg: GetLinkOnSameOrg = (pathEnd) => {
  const { organizationId } = useOrgFromPath();
  return getOrgSpecificLink({ organizationId, pathEnd });
};

/** To help create dynamic links to navigate between different sub pages on the same org, while avoiding relative links. */
export function useDynamicLinkOnSameOrg() {
  const { organizationId } = useOrgFromPath();

  const getLinkOnSameOrg = useCallback<GetLinkOnSameOrg>(
    (pathEnd) => getOrgSpecificLink({ organizationId, pathEnd }),
    [organizationId],
  );

  return { getLinkOnSameOrg };
}

export function useLinkOnSameOrgLogConfig() {
  const { organizationId } = useOrgFromPath();
  const { logId } = useLogIdFromPath();
  return getOrgSpecificLink({
    organizationId,
    pathEnd: `/data-setup/logs/${logId}/edit`,
  });
}

export function useDataSourceLinkOnSameOrg({
  dataSourceType,
  dataSourceId,
}: {
  dataSourceType?: DataSourceTypeUrl;
  dataSourceId?: string;
}) {
  const { organizationId } = useOrgFromPath();

  if (!dataSourceType || !dataSourceId) {
    return null;
  }

  if (dataSourceType === DataSourceTypeUrl.RESOURCE_TYPES) {
    return getOrgSpecificLink({
      organizationId,
      pathEnd: `/data-setup/${dataSourceType}/${dataSourceId}/resources/pages/0`,
    });
  }

  return getOrgSpecificLink({
    organizationId,
    pathEnd: `/data-setup/${dataSourceType}/${dataSourceId}`,
  });
}
